import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Construction } from "lucide-react";

// Helper function to get the first sentence
const getFirstSentence = (text) => {
  // Match the first sentence (ending with .!? followed by a space or end of string)
  const match = text.match(/^[^.!?]+[.!?](?:\s|$)/);
  return match ? match[0].trim() : text;
};

const Projects = () => {
  const [projects, setProjects] = useState([]);
  const [projectStatus, setProjectStatus] = useState({});
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadProjects = async () => {
      try {
        const response = await fetch('/projects/projects.json');
        const data = await response.json();
        setProjects(data.projects);
        
        // Initialize status for all projects
        const initialStatus = {};
        data.projects.forEach(project => {
          initialStatus[project.id] = null; // null means we don't know yet
        });
        setProjectStatus(initialStatus);
        
        // Check images in parallel
        const statusPromises = data.projects.map(async (project) => {
          try {
            const img = new Image();
            await new Promise((resolve) => {
              img.onload = () => resolve(true);
              img.onerror = () => resolve(false);
              img.src = `${project.basePath}/preview.webp`;
            });
            return { id: project.id, exists: true };
          } catch {
            return { id: project.id, exists: false };
          }
        });

        const results = await Promise.all(statusPromises);
        const newStatus = { ...initialStatus };
        results.forEach(({ id, exists }) => {
          newStatus[id] = exists;
        });
        setProjectStatus(newStatus);
      } catch (error) {
        console.error('Error loading projects:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadProjects();
  }, []);

  if (isLoading) {
    return (
      <div className="page-container">
        <div className="container mx-auto px-4">
          <div className="animate-pulse">
            <div className="h-12 bg-primary/10 rounded-lg w-1/3 mb-8"></div>
            <div className="grid md:grid-cols-2 gap-8">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="glass-card p-6">
                  <div className="w-full h-48 bg-primary/5 rounded-lg mb-4"></div>
                  <div className="h-6 bg-primary/10 rounded w-3/4 mb-2"></div>
                  <div className="h-4 bg-primary/5 rounded w-full mb-4"></div>
                  <div className="flex gap-2">
                    <div className="h-6 bg-primary/5 rounded w-16"></div>
                    <div className="h-6 bg-primary/5 rounded w-16"></div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="container mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <h1 className="text-4xl md:text-5xl font-bold text-gradient mb-8">
            Gallery
          </h1>

          <div className="grid md:grid-cols-2 gap-8">
            {projects.map((project) => (
              <div key={project.id}>
                {projectStatus[project.id] === true ? (
                  <Link to={`/project/${project.id}`}>
                    <motion.div
                      whileHover={{ y: -10 }}
                      className="project-card glass-card p-6"
                    >
                      <img  
                        alt={project.title} 
                        className="w-full h-48 object-cover rounded-lg mb-4"
                        src={`${project.basePath}/preview.webp`}
                        onError={(e) => {
                          e.target.style.display = 'none';
                          setProjectStatus(prev => ({
                            ...prev,
                            [project.id]: false
                          }));
                        }}
                      />
                      <h3 className="text-xl font-semibold mb-2">{project.title}</h3>
                      <p className="text-muted-foreground mb-4">{getFirstSentence(project.description)}</p>
                      <div className="flex flex-wrap gap-2">
                        {project.tools.map((tool) => (
                          <span
                            key={tool}
                            className="px-3 py-1 text-sm bg-primary/10 text-primary rounded-full"
                          >
                            {tool}
                          </span>
                        ))}
                      </div>
                    </motion.div>
                  </Link>
                ) : (
                  <div className="project-card glass-card p-6 opacity-75">
                    <div className="w-full h-48 bg-primary/5 rounded-lg mb-4 flex items-center justify-center">
                      <Construction className="w-12 h-12 text-primary/50" />
                    </div>
                    <h3 className="text-xl font-semibold mb-2 flex items-center gap-2">
                      {project.title}
                      <span className="text-sm font-normal text-primary">
                        (Coming Soon)
                      </span>
                    </h3>
                    <p className="text-muted-foreground mb-4">Project in progress...</p>
                    <div className="flex flex-wrap gap-2">
                      {project.tools.map((tool) => (
                        <span
                          key={tool}
                          className="px-3 py-1 text-sm bg-primary/5 text-primary/50 rounded-full"
                        >
                          {tool}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Projects;
